import { useEffect, useState } from "react";
import { Shield, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, ChevronDown, ChevronRight } from "lucide-react";
import { groundOpsApi } from "@/api";

const GRADE_ICON = {
  "Excellent": CheckCircle,
  "Good":      CheckCircle,
  "At Risk":   AlertTriangle,
  "Poor":      AlertTriangle,
};

function VendorCard({ vendor, expanded, onToggle }) {
  const Icon = GRADE_ICON[vendor.grade] || CheckCircle;
  const pct = vendor.on_time_pct;
  const slaGap = pct - vendor.sla_target_pct;

  return (
    <div
      className={`bg-[#0c1929] border rounded-xl overflow-hidden transition-all ${vendor.grade === "Poor" ? "border-red-500/30" : vendor.grade === "At Risk" ? "border-amber-500/25" : "border-slate-700/30"}`}
      data-testid={`vendor-card-${vendor.name.toLowerCase().replace(/\s/g,"-")}`}
    >
      {/* Header row */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition"
        onClick={onToggle}
      >
        {/* Score ring */}
        <div className={`relative flex items-center justify-center w-14 h-14 rounded-full ring-2 ${vendor.grade_badge.includes("green") ? "ring-green-500/30" : vendor.grade_badge.includes("amber") ? "ring-amber-500/30" : vendor.grade_badge.includes("red") ? "ring-red-500/30" : "ring-teal-500/30"} bg-black/20`}>
          <span className={`text-lg font-black font-mono ${vendor.grade_color}`}>{vendor.sla_score}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-sm text-white">{vendor.name}</h3>
            <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase px-1.5 py-0.5 rounded border ${vendor.grade_badge}`}>
              <Icon size={9} />
              {vendor.grade}
            </span>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">{vendor.service} · {vendor.stations.join(", ")}</div>
        </div>

        {/* Stats */}
        <div className="hidden sm:grid grid-cols-3 gap-6 text-center">
          <div>
            <div className={`text-base font-black font-mono ${pct >= vendor.sla_target_pct ? "text-green-400" : "text-red-400"}`}>{pct}%</div>
            <div className="text-[8px] text-slate-600 uppercase">On-Time</div>
          </div>
          <div>
            <div className="text-base font-black font-mono text-slate-300">{vendor.tasks_total}</div>
            <div className="text-[8px] text-slate-600 uppercase">Tasks</div>
          </div>
          <div>
            <div className={`text-base font-black font-mono ${vendor.avg_delay_mins > 0 ? "text-amber-400" : "text-green-400"}`}>
              {vendor.avg_delay_mins > 0 ? `+${vendor.avg_delay_mins}m` : "—"}
            </div>
            <div className="text-[8px] text-slate-600 uppercase">Avg Delay</div>
          </div>
        </div>

        {/* SLA bar */}
        <div className="w-32 hidden md:block">
          <div className="flex justify-between text-[9px] text-slate-500 mb-1">
            <span>SLA: {vendor.sla_target_pct}%</span>
            <span className={slaGap >= 0 ? "text-green-400" : "text-red-400"}>
              {slaGap >= 0 ? `+${slaGap}` : slaGap}
            </span>
          </div>
          <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${pct >= vendor.sla_target_pct ? "bg-green-500" : pct >= vendor.sla_target_pct - 8 ? "bg-amber-500" : "bg-red-500"}`}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
          <div className="mt-0.5 text-[8px] text-slate-600 text-right">SLA target line at {vendor.sla_target_pct}%</div>
        </div>

        <div className="text-slate-600 ml-2">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
      </div>

      {/* Expanded: recent delays */}
      {expanded && (
        <div className="px-5 pb-4 border-t border-slate-700/20 bg-black/10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-b border-slate-700/15 mb-3">
            {[
              { label: "Total Tasks",   val: vendor.tasks_total,   cls: "text-white"      },
              { label: "On Time",       val: vendor.tasks_on_time,  cls: "text-green-400"  },
              { label: "Delayed",       val: vendor.tasks_delayed,  cls: vendor.tasks_delayed > 0 ? "text-amber-400" : "text-slate-500" },
              { label: "Blocked",       val: vendor.tasks_blocked,  cls: vendor.tasks_blocked > 0 ? "text-red-400" : "text-slate-500"   },
            ].map(({ label, val, cls }) => (
              <div key={label} className="text-center">
                <div className={`text-xl font-black font-mono ${cls}`}>{val}</div>
                <div className="text-[8px] uppercase text-slate-600">{label}</div>
              </div>
            ))}
          </div>

          {vendor.recent_delays.length > 0 ? (
            <>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-2">Recent Issues</div>
              <div className="space-y-1.5">
                {vendor.recent_delays.map((t, i) => (
                  <div key={i} className={`flex items-start gap-2 text-[10px] rounded-lg px-3 py-2 ${t.status === "blocked" ? "bg-purple-500/10 text-purple-300" : "bg-red-500/10 text-red-300"}`}>
                    <AlertTriangle size={9} className="flex-shrink-0 mt-0.5" />
                    <span className="font-semibold">{t.name}</span>
                    <span className="uppercase text-[8px] opacity-60 ml-1">{t.status}</span>
                    {t.delay_minutes > 0 && <span className="ml-auto font-mono">+{t.delay_minutes}m</span>}
                    {t.comments && <span className="text-[9px] opacity-60 truncate max-w-xs">{t.comments}</span>}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-[10px] text-slate-600 py-2">No recent issues recorded for this vendor.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VendorSLA() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expandedIdx, setExpand] = useState(null);

  useEffect(() => {
    groundOpsApi.getVendorSla().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-slate-500 animate-pulse">Loading vendor data...</div>;
  if (!data)   return <div className="p-10 text-center text-red-400">Failed to load vendor SLA</div>;

  const { vendors, overall_compliance_pct, vendors_at_risk } = data;

  return (
    <div className="p-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-base font-black uppercase tracking-widest text-white flex items-center gap-2">
            <Shield size={16} className="text-orange-400" /> Vendor SLA Manager
          </h1>
          <p className="text-[10px] text-slate-500 mt-0.5">Ground handling vendor compliance · Today's turnarounds</p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-black font-mono ${overall_compliance_pct >= 85 ? "text-green-400" : overall_compliance_pct >= 70 ? "text-amber-400" : "text-red-400"}`}>
            {overall_compliance_pct}%
          </div>
          <div className="text-[8px] uppercase text-slate-500 tracking-wider">Overall Compliance</div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: "Vendors Tracked",  val: data.total_vendors,    color: "text-white"       },
          { label: "Meeting SLA",      val: data.total_vendors - vendors_at_risk, color: "text-green-400"  },
          { label: "Needs Attention",  val: vendors_at_risk,       color: vendors_at_risk > 0 ? "text-red-400" : "text-slate-500" },
        ].map(({ label, val, color }) => (
          <div key={label} className="bg-[#0c1929] border border-slate-700/30 rounded-lg px-4 py-3" data-testid={`sla-kpi-${label.toLowerCase().replace(/\s/g,"-")}`}>
            <div className={`text-2xl font-black font-mono ${color}`}>{val}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Vendor cards */}
      <div className="space-y-3" data-testid="vendor-list">
        {vendors.map((v, i) => (
          <VendorCard
            key={v.name}
            vendor={v}
            expanded={expandedIdx === i}
            onToggle={() => setExpand(expandedIdx === i ? null : i)}
          />
        ))}
      </div>

      {/* SLA legend */}
      <div className="flex items-center gap-6 mt-5 text-[9px] text-slate-600 flex-wrap">
        <span className="uppercase tracking-wider font-bold">SLA Score:</span>
        {[["Excellent", "≥ target", "text-green-400"], ["Good", "≤ 8% below", "text-teal-400"], ["At Risk", "9–15% below", "text-amber-400"], ["Poor", "> 15% below", "text-red-400"]].map(([g, d, c]) => (
          <span key={g} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${c.replace("text-","bg-")}`} />
            <span className={c}>{g}</span>
            <span className="text-slate-700">({d})</span>
          </span>
        ))}
      </div>
    </div>
  );
}
